import React, { useEffect, useState } from "react";
import zrsLogo from "@/brand/zrsLogo";
import { useI18n } from "@/i18n/I18nProvider";

export default function LoadingIntro({ onDone }) {
    const [pct, setPct] = useState(0);
    const [fading, setFading] = useState(false);
    const complete = pct >= 100;
    const { t } = useI18n();

    useEffect(() => {
        let raf;
        const start = performance.now();
        const dur = 1600;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - t, 2);
            setPct(Math.round(eased * 100));
            if (t < 1) raf = requestAnimationFrame(tick);
            else {
                setTimeout(() => setFading(true), 420);
                setTimeout(() => onDone && onDone(), 900);
            }
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [onDone]);

    return (
        <div
            data-testid="loading-intro"
            className="fixed inset-0 z-[200] flex flex-col items-center justify-center"
            style={{
                background:
                    "radial-gradient(ellipse at center, #1A0F0A 0%, #0A0604 60%, #050505 100%)",
                opacity: fading ? 0 : 1,
                transition: "opacity 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                pointerEvents: fading ? "none" : "auto",
            }}
        >
            <div className="relative flex flex-col items-center gap-10 px-8">
                {/* Logo — simple fade in, stronger glow at 100% */}
                <img
                    src={zrsLogo}
                    alt="ZRS"
                    className="w-40 h-40 md:w-52 md:h-52 object-contain select-none"
                    draggable={false}
                    style={{
                        opacity: 0,
                        animation: "fadeIn 700ms cubic-bezier(0.22, 1, 0.36, 1) 80ms forwards",
                        filter: complete
                            ? "drop-shadow(0 0 45px rgba(255,184,0,0.8)) drop-shadow(0 0 90px rgba(255,184,0,0.45))"
                            : "drop-shadow(0 0 25px rgba(255,184,0,0.4)) drop-shadow(0 0 55px rgba(255,184,0,0.22))",
                        transition: "filter 500ms ease",
                    }}
                />

                {/* Bar + percentage */}
                <div className="w-64 md:w-80">
                    <div
                        className="h-[3px] w-full rounded-full overflow-hidden"
                        style={{ background: "rgba(255,255,255,0.06)" }}
                    >
                        <div
                            className="h-full"
                            style={{
                                width: `${pct}%`,
                                background:
                                    "linear-gradient(90deg, #FFB800 0%, #FFD45A 100%)",
                                boxShadow: complete
                                    ? "0 0 26px rgba(255,184,0,0.9)"
                                    : "0 0 12px rgba(255,184,0,0.55)",
                                transition:
                                    "width 90ms linear, box-shadow 500ms ease",
                            }}
                        />
                    </div>

                    <div className="mt-4 flex justify-between text-[11px] tracking-brand uppercase">
                        <span className="text-zrs-metal">{t("intro.loading")}</span>
                        <span
                            data-testid="loading-percent"
                            className="text-gold font-semibold tabular-nums"
                            style={{
                                textShadow: complete
                                    ? "0 0 14px rgba(255,184,0,0.75)"
                                    : "none",
                                transition: "text-shadow 400ms ease",
                            }}
                        >
                            {pct}%
                        </span>
                    </div>
                </div>

                <div className="text-[10px] uppercase tracking-[0.4em] text-zrs-metal/80">
                    Zaralha Servers
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
            `}</style>
        </div>
    );
}
